/**
 * AdminModifierGroups
 * -------------------
 * Admin CRUD for reusable modifier groups (e.g. "Sauces", "Salad",
 * "Extras"). Products and bundle options can reference these by id so
 * choices are defined once and reused everywhere.
 *
 * Delete is guarded by the backend — if a group is referenced by any
 * product or bundle option, the API returns 400 with a helpful message.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { useAuth } from '../../context/AuthContext';
import { bundleAPI } from '../../utils/api';

const empty = () => ({
  name: '',
  title: '',
  instructions: '',
  type: 'checkbox',
  required: false,
  min_selections: 0,
  max_selections: 5,
  choices: [],
  active: true,
});

const AdminModifierGroups = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchAll();
  }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const res = await bundleAPI.listModifierGroups(true);
      setGroups(res.data || []);
    } catch (e) {
      toast.error('Failed to load modifier groups');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty());
    setDialogOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name,
      title: g.title,
      instructions: g.instructions || '',
      type: g.type,
      required: !!g.required,
      min_selections: g.min_selections || 0,
      max_selections: g.max_selections || 1,
      choices: (g.choices || []).map((c) => ({ ...c })),
      active: g.active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.title) {
      toast.error('Name and title are required');
      return;
    }
    try {
      if (editing) {
        await bundleAPI.updateModifierGroup(editing.id, form);
        toast.success('Updated');
      } else {
        await bundleAPI.createModifierGroup(form);
        toast.success('Created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete modifier group "${g.title || g.name}"?`)) return;
    try {
      await bundleAPI.deleteModifierGroup(g.id);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const addChoice = () => {
    setForm({ ...form, choices: [...form.choices, { name: '', price: 0, active: true }] });
  };
  const updateChoice = (i, patch) => {
    const next = form.choices.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    setForm({ ...form, choices: next });
  };
  const removeChoice = (i) => {
    setForm({ ...form, choices: form.choices.filter((_, idx) => idx !== i) });
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Modifier Groups" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <Button className="rounded-full" onClick={openCreate} data-testid="add-modifier-group">
            <Plus className="h-4 w-4 mr-1" /> Add group
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Define a group once and reuse it on any standard product or bundle option.
        </p>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed rounded-xl">
            <p className="text-muted-foreground mb-4">No modifier groups yet.</p>
            <Button className="rounded-full" onClick={openCreate}>
              Create your first modifier group
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groups.map((g) => (
              <div
                key={g.id}
                className={`bg-white rounded-xl border p-4 ${
                  g.active ? '' : 'opacity-60'
                }`}
                data-testid="modifier-group-card"
              >
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base truncate">{g.title || g.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      Internal name: {g.name} · {g.type} · {g.required ? 'required' : 'optional'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)} data-testid="edit-modifier-group">Edit</Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(g)}
                      className="h-8 w-8"
                      data-testid="delete-modifier-group"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  {(g.choices || []).map((c) => (
                    <div
                      key={c.name}
                      className={`flex items-center justify-between text-xs ${c.active === false ? 'text-muted-foreground line-through' : ''}`}
                    >
                      <span>{c.name}</span>
                      <span>{Number(c.price) > 0 ? `+£${Number(c.price).toFixed(2)}` : 'Included'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit modifier group' : 'New modifier group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Internal name</Label>
              <Input
                data-testid="modifier-group-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. sauces_v1"
              />
            </div>
            <div>
              <Label>Customer-facing title</Label>
              <Input
                data-testid="modifier-group-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Choose your sauce"
              />
            </div>
            <div>
              <Label>Instructions (optional)</Label>
              <Textarea
                rows={2}
                value={form.instructions}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Selection type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radio">Single choice</SelectItem>
                    <SelectItem value="checkbox">Multiple choice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Required</Label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={form.required}
                    onChange={(e) => setForm({ ...form, required: e.target.checked })}
                  />
                  <span className="text-sm text-muted-foreground">Customer must pick</span>
                </div>
              </div>
              <div>
                <Label>Min selections</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.min_selections}
                  onChange={(e) => setForm({ ...form, min_selections: Math.max(0, Number(e.target.value) || 0) })}
                />
              </div>
              <div>
                <Label>Max selections</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.max_selections}
                  onChange={(e) => setForm({ ...form, max_selections: Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Choices</Label>
                <Button type="button" size="sm" variant="outline" onClick={addChoice} data-testid="add-choice">
                  <Plus className="h-3 w-3 mr-1" /> Add choice
                </Button>
              </div>
              {form.choices.length === 0 && (
                <p className="text-xs text-muted-foreground">No choices yet.</p>
              )}
              <div className="space-y-2">
                {form.choices.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      placeholder="Name"
                      data-testid="choice-name-input"
                      value={c.name}
                      onChange={(e) => updateChoice(i, { name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      placeholder="0.00"
                      data-testid="choice-price-input"
                      type="number"
                      step="0.01"
                      min={0}
                      value={c.price}
                      onChange={(e) => updateChoice(i, { price: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-24"
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={c.active !== false}
                        onChange={(e) => updateChoice(i, { active: e.target.checked })}
                      />
                      On
                    </label>
                    <Button variant="ghost" size="icon" onClick={() => removeChoice(i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Group active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-modifier-group">
              {editing ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminModifierGroups;
